import { getKitField, KitField, kitFieldsForForm } from "../../form/kitFieldRegistry";
import { localTimeZoneHint } from "./dateTime";
import { createDomFieldAdapter } from "./DomFieldAdapter";
import { createKitFieldAdapter } from "./KitFieldAdapter";
import { ExcludedScope, excludedScopeOf, exclusionFor, MAX_WIRE_PATH_LENGTH, WIRE_PATH_FOREIGN_CHARACTERS, WIRE_PATH_PATTERN } from "./exclusions";
import { formTitle, labelFor } from "./labels";
import type { DiscoveredField, DiscoveredForm, FieldAdapter, FieldDescriptor } from "./types";

export const FORM_SELECTOR = ".content-page form";
const FIELD_SELECTOR = "input:not([type=hidden]):not([type=submit]):not([type=button]):not([type=reset]), select, textarea, [data-field-id]";
const RJSF_ID_PREFIX = "root_";
const HASH_HEX_LENGTH = 8;
const HASH_SEPARATOR = "-";
const HASHED_PATH_STEM_LENGTH = MAX_WIRE_PATH_LENGTH - HASH_SEPARATOR.length - HASH_HEX_LENGTH;

/** 32-bit FNV-1a, as a lowercase hex string — small, dependency-free and stable across sessions. */
export function fnv1a32(text: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16);
}

/** The field path an element declares: kit model, framework name, else its id (rjsf ids spell the path with underscores). */
export function pathOf(element: HTMLElement): string | undefined {
  const model = element.getAttribute("data-model");
  if (model) {
    return model;
  }
  const name = element.getAttribute("name");
  if (name) {
    return name;
  }
  const id = element.id;
  if (!id) {
    return undefined;
  }
  return id.startsWith(RJSF_ID_PREFIX) ? id.slice(RJSF_ID_PREFIX.length).split("_").join(".") : id;
}

/**
 * A kit input's path from the page form's model: inside a list item the input's own model is
 * relative to the item, so the item's prefix goes in front — composed the way FormList composes it.
 */
export function qualifiedPath(handle: KitField): string | undefined {
  const prefix = handle.fieldSet.modelPrefix;
  if (!prefix) {
    return handle.model;
  }
  return handle.model ? `${prefix}.${handle.model}` : prefix;
}

/**
 * The path as it travels: unchanged when the server accepts it, otherwise a readable stem plus a
 * hash of the full path, so a map key such as `a::b` keeps one stable, distinct identity across
 * discoveries instead of being dropped by the server. Exclusions judge the original path.
 */
export function wirePath(path: string): string {
  if (WIRE_PATH_PATTERN.test(path)) {
    return path;
  }
  const stem = path.replace(WIRE_PATH_FOREIGN_CHARACTERS, "_").slice(0, HASHED_PATH_STEM_LENGTH);
  return `${stem}${HASH_SEPARATOR}${fnv1a32(path).padStart(HASH_HEX_LENGTH, "0")}`;
}

/** A kit input with a live handle; the element is absent for inputs that render no attributable element. */
interface KitCandidate {
  kind: "kit";
  element: HTMLElement | undefined;
  handle: KitField;
  path: string;
}

/** Any other rendered input, described from the DOM alone. */
interface DomCandidate {
  kind: "dom";
  element: HTMLElement;
  path: string;
}

type Candidate = KitCandidate | DomCandidate;

/**
 * A handle counts as the page's field when its element sits directly under the form and it is bound
 * to the form's model — list items included, they report the page form's id. A fieldSet of its own
 * (a map add-row) has its own id.
 */
function isPageLevel(form: HTMLFormElement, candidate: Candidate): boolean {
  if (candidate.kind === "kit") {
    return candidate.element?.closest("[data-form-id]") === form && candidate.handle.formId === form.getAttribute("data-form-id");
  }
  return true;
}

function adapterFor(candidate: Candidate): FieldAdapter {
  return candidate.kind === "kit"
    ? createKitFieldAdapter(candidate.handle, candidate.element, candidate.path)
    : createDomFieldAdapter(candidate.element, candidate.path);
}

/** A described field and whether it belongs to the page form, which decides who wins a shared path. */
interface FieldWithLevel {
  field: DiscoveredField;
  pageLevel: boolean;
}

function describeField(form: HTMLFormElement, candidate: Candidate, excludedScopes: readonly ExcludedScope[]): FieldWithLevel {
  const { element } = candidate;
  const path = wirePath(candidate.path);
  const adapter = adapterFor({ ...candidate, path });
  const pageLevel = isPageLevel(form, candidate);
  const intrinsic = adapter.describe();
  const descriptor: FieldDescriptor = { path, kind: intrinsic.kind, label: labelFor(element, path) };
  const hint = element?.getAttribute("data-ai-hint") || (adapter.kind === "datetime" ? localTimeZoneHint() : undefined);
  if (hint) {
    descriptor.hint = hint;
  }
  if (element?.getAttribute("data-ai-format") === "epoch-ms") {
    descriptor.format = "epoch-ms";
  }
  const reason = exclusionFor({ element, form, path: candidate.path, kind: adapter.kind, pageLevel, excludedScopes });
  if (intrinsic.required) {
    descriptor.required = true;
  }
  if (intrinsic.readOnly) {
    descriptor.readOnly = true;
  }
  if (reason) {
    descriptor.excluded = true;
    descriptor.reason = reason;
    return { field: { path, adapter, descriptor }, pageLevel };
  }
  if (intrinsic.value !== undefined) {
    descriptor.value = intrinsic.value;
  }
  if (intrinsic.options) {
    descriptor.options = intrinsic.options;
  }
  if (intrinsic.error) {
    descriptor.error = intrinsic.error;
  }
  return { field: { path, adapter, descriptor }, pageLevel };
}

function candidatesIn(form: HTMLFormElement): Candidate[] {
  const candidates: Candidate[] = [];
  const seenHandles = new Set<string>();
  Array.from(form.querySelectorAll<HTMLElement>(FIELD_SELECTOR)).forEach((element) => {
    // react-select renders its own text input inside the kit wrapper; the wrapper is the field.
    const owner = element.closest("[data-field-id]");
    if (owner && owner !== element) {
      return;
    }
    const fieldId = element.getAttribute("data-field-id");
    const handle = fieldId ? getKitField(fieldId) : undefined;
    if (handle) {
      seenHandles.add(handle.fieldId);
    }
    const path = (handle ? qualifiedPath(handle) : undefined) ?? pathOf(element);
    if (!path) {
      return;
    }
    candidates.push(handle ? { kind: "kit", element, handle, path } : { kind: "dom", element, path });
  });
  const formId = form.getAttribute("data-form-id");
  if (formId) {
    // An input that renders no attributable element exists only in the registry.
    kitFieldsForForm(formId)
      .filter((handle) => !seenHandles.has(handle.fieldId))
      .forEach((handle) => {
        const path = qualifiedPath(handle);
        if (path) {
          candidates.push({ kind: "kit", element: undefined, handle, path });
        }
      });
  }
  return candidates;
}

// A path is listed once. The page form's own field wins over one bound to a fieldSet of its own
// (a map add-row's `key` / `value`), which must never shadow a page field of the same name.
function uniqueByPath(described: FieldWithLevel[]): DiscoveredField[] {
  const chosen = new Map<string, FieldWithLevel>();
  described.forEach((candidate) => {
    const current = chosen.get(candidate.field.path);
    if (!current || (!current.pageLevel && candidate.pageLevel)) {
      chosen.set(candidate.field.path, candidate);
    }
  });
  return described.filter((candidate) => chosen.get(candidate.field.path) === candidate).map((candidate) => candidate.field);
}

// A framework form has no identity of its own; its position plus the set of paths it renders is
// stable across re-renders and distinguishes the list-view forms that share a page.
function domFormId(index: number, fields: DiscoveredField[]): string {
  const paths = fields.map((field) => field.path).sort();
  return `dom-${index}-${fnv1a32(paths.join("|"))}`;
}

// A map excluded on one form covers its model on every form of the page: another input bound to
// the same model (a role select next to the attributes map) would otherwise leak a map value.
function excludedMapScopes(forms: HTMLFormElement[]): ExcludedScope[] {
  return forms
    .flatMap((form) => Array.from(form.querySelectorAll<HTMLElement>("[data-ai-map]")))
    .map(excludedScopeOf)
    .filter((scope): scope is ExcludedScope => scope !== undefined);
}

/**
 * Every form on the page with the fields it renders, in document order. Only rendered inputs are
 * read — a form's model object may hold more than it binds — and the registry upgrades a kit
 * input to model-space access when its handle is live.
 */
export function discoverForms(root: ParentNode = document): DiscoveredForm[] {
  const forms = Array.from(root.querySelectorAll<HTMLFormElement>(FORM_SELECTOR));
  const excludedScopes = excludedMapScopes(forms);
  return forms.map((form, index) => {
    const fields = uniqueByPath(candidatesIn(form).map((candidate) => describeField(form, candidate, excludedScopes)));
    const kitFormId = form.getAttribute("data-form-id");
    return {
      formId: kitFormId ?? domFormId(index, fields),
      title: formTitle(form, index, root),
      element: form,
      kit: kitFormId !== null,
      fields,
    };
  });
}

/** The discovered form rendered by a given element, if any. */
export function findForm(forms: DiscoveredForm[], element: HTMLFormElement): DiscoveredForm | undefined {
  return forms.find((form) => form.element === element);
}
